import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE, EDIT, ADD, DELETE, LIST, YES,
} from "const"
import {
    LifecyclePhase,
    LifecyclePhaseCreate,
    LifecycleInputDocument,
    LifecycleOutputDocument,
    LifecycleInputDocumentCreate,
    LifecycleOutputDocumentCreate,
    MtDriveFile,
    MtTask,
    CcRequirement,
    CcRequirementLifecyclePhaseCreate,
} from "server/models"
import {
    StateType,
    useInput,
    hasAccess,
    hasProjectAccess,
    hasLifecycleAccess,
} from "client/utils"
import {
    PRM_LIFECYCLE,
    PRM_LIFECYCLE_PHASE_LIST,
    PRM_LIFECYCLE_POPULATE,
    PRM_TASK,
    PRM_CC_REQUIREMENT,
    StoreType,
    LifecyclePhaseValidation,
    TasksOnSelect,
    RequirementsOnSelect,

    IsOwnerLifecycle,
    IsResponsibleLifecycle,
    UpdateLifecyclePhase,
    SaveLifecyclePhaseInputDocuments,
    SaveLifecyclePhaseOutputDocuments,
    ClearTaskLifecyclePhase,
    UpdateTaskLifecyclePhase,
    DeleteCcRequirementLifecyclePhases,
    SaveCcRequirementLifecyclePhases,

    setLifecyclePhaseConfirmDelete,
    setLifecycleModelReload,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    LabelButton,
    Input,
    Textarea,
    DivInput,
    UserIcon,
    Feedback,
    Delimiter,
    UserDropdown,
    Button,
    SpinnerIcon,
} from "client/components"

import PopulateDocuments from "./populateDocuments"
import LinkTasks from "./linkTasks"
import LinkRequirements from "./linkRequirements"
import { UpdateConnectionPoints } from "../canvas/actions/connectionPoints"


interface EditLifecyclePhaseProps {
    phase: LifecyclePhase
}

let isMounted: boolean

const Form = ({
    phase,
}: EditLifecyclePhaseProps) => {
    const lifecycle = useSelector((state: StoreType) => state.lifecycle.lifecycleView)

    const userList = useSelector((state: StoreType) => state.user.userList)
    const accountUserList = useSelector((state: StoreType) => state.accountUser.accountUserList)
    const projectUserList = useSelector((state: StoreType) => state.projectUser.projectUserList)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(phase.name)
    const [objective, setObjective] = useInput(phase.objective)

    const [responsible, setResponsible] = useState(phase.responsible)
    const [inputDocuments, setInputDocuments] = useState(phase.inputDocuments || [])
    const [outputDocuments, setOutputDocuments] = useState(phase.outputDocuments || [])
    const [linkedTasks, setLinkedTasks] = useState(phase.tasks || [])
    const [linkedRequirements, setLinkedRequirements] = useState<readonly CcRequirement[]>([])

    const [validation, setValidation] = useState<LifecyclePhaseValidation>(null)
    const [saveState, setSaveState] = useState<StateType>(IDLE)

    useEffect(() => {
        const requirements: CcRequirement[] = []
        phase.requirementLifecyclePhases.forEach((row) => {
            requirements.push(row.requirement)
        })
        setLinkedRequirements(requirements)

        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onSave = () => {
        setSaveState(PENDING)

        const newPhaseData: LifecyclePhaseCreate = {
            lifecycleId: phase.lifecycleId,
            phaseId: phase.phaseId,
            position: phase.position,
            name,
            objective,
            creator: phase.creator,
            responsible,
        }

        UpdateLifecyclePhase(phase.id, newPhaseData, lang)
        .then((res) => {
            if (isMounted) {
                setValidation(res.validation)
            }
            if (res.validation) {
                if (isMounted) setSaveState(IDLE)
                return
            }

            const inputDocs: LifecycleInputDocumentCreate[] = []
            inputDocuments.forEach((row) => {
                inputDocs.push({
                    phaseId: row.phaseId,
                    fileId: row.fileId,
                    creator: row.creator,
                })
            })

            const outputDocs: LifecycleOutputDocumentCreate[] = []
            outputDocuments.forEach((row) => {
                outputDocs.push({
                    phaseId: row.phaseId,
                    fileId: row.fileId,
                    creator: row.creator,
                })
            })

            const requirementData: CcRequirementLifecyclePhaseCreate[] = []
            linkedRequirements.forEach((requirement) => {
                requirementData.push({
                    requirementId: requirement.id,
                    phaseId: phase.id,
                })
            })

            Promise.all([
                UpdateConnectionPoints(phase.id, name, phase.positionObject),
                SaveLifecyclePhaseInputDocuments(phase.id, inputDocs),
                SaveLifecyclePhaseOutputDocuments(phase.id, outputDocs),
                ClearTaskLifecyclePhase(phase.id).then(() => {
                    const taskPromiseArray: Promise<any>[] = []
                    linkedTasks.forEach((task) => {
                        taskPromiseArray.push(UpdateTaskLifecyclePhase(task.id, phase.id))
                    })
                    return Promise.all(taskPromiseArray)
                }),
                DeleteCcRequirementLifecyclePhases(null, phase.id).then(() => (
                    SaveCcRequirementLifecyclePhases(requirementData)
                )),
            ]).then(() => {
                if (isMounted) {
                    setSaveState(IDLE)
                }

                dispatch(setLifecycleModelReload(false))
                setTimeout(() => {
                    dispatch(setLifecycleModelReload(true))
                }, 0)
            })
        })
    }

    const onDelete = () => {
        dispatch(setLifecyclePhaseConfirmDelete(phase))
    }

    const addInputDocument = (file: MtDriveFile) => {
        const addedDocument = inputDocuments.find((row) => row.fileId === file.id)
        if (addedDocument) return

        const newDocument: LifecycleInputDocument = {
            phaseId: phase.id,
            fileId: file.id,
            creator: userData.userId,
            file,
        }
        const newInputDocuments = inputDocuments.slice()
        newInputDocuments.push(newDocument)
        setInputDocuments(newInputDocuments)
    }

    const addOutputDocument = (file: MtDriveFile) => {
        const addedDocument = outputDocuments.find((row) => row.fileId === file.id)
        if (addedDocument) return

        const newDocument: LifecycleOutputDocument = {
            phaseId: phase.id,
            fileId: file.id,
            creator: userData.userId,
            file,
        }
        const newOutputDocuments = outputDocuments.slice()
        newOutputDocuments.push(newDocument)
        setOutputDocuments(newOutputDocuments)
    }

    const addTasks: TasksOnSelect = (tasks: MtTask[]) => {
        const addedIds: number[] = []
        linkedTasks.forEach((task) => addedIds.push(task.id))

        const newLinkedTasks = linkedTasks.slice()
        tasks.forEach((task) => {
            if (!addedIds.includes(task.id)) newLinkedTasks.push(task)
        })
        setLinkedTasks(newLinkedTasks)
    }

    const addRequirements: RequirementsOnSelect = (requirements: CcRequirement[]) => {
        const addedIds: number[] = []
        linkedRequirements.forEach((requirement) => addedIds.push(requirement.id))

        const newLinkedRequirements = linkedRequirements.slice()
        requirements.forEach((requirement) => {
            if (!addedIds.includes(requirement.id)) newLinkedRequirements.push(requirement)
        })
        setLinkedRequirements(newLinkedRequirements)
    }


    const deleteInputDocument = (document: LifecycleInputDocument) => {
        setInputDocuments(inputDocuments.filter((row) => row.fileId !== document.fileId))
    }

    const deleteOutputDocument = (document: LifecycleOutputDocument) => {
        setOutputDocuments(outputDocuments.filter((row) => row.fileId !== document.fileId))
    }

    const deleteTask = (task: MtTask) => {
        setLinkedTasks(linkedTasks.filter((row) => row.id !== task.id))
    }

    const deleteRequirement = (requirement: CcRequirement) => {
        setLinkedRequirements(linkedRequirements.filter((row) => row.id !== requirement.id))
    }


    const lifecycleEditAccess = !project.id
        ? hasAccess(PRM_LIFECYCLE, EDIT)
        : hasProjectAccess(PRM_LIFECYCLE, EDIT)

    const phaseListEditAccess = hasLifecycleAccess(PRM_LIFECYCLE_PHASE_LIST, EDIT)
    const populateAddAccess = hasLifecycleAccess(PRM_LIFECYCLE_POPULATE, ADD)
    const populateDeleteAccess = hasLifecycleAccess(PRM_LIFECYCLE_POPULATE, DELETE)

    const isOwner = IsOwnerLifecycle(project.id, lifecycle, userData)
    const isResponsible = IsResponsibleLifecycle(lifecycle, userData)
    const isProjectResponsible = project.id && isResponsible

    const allowedToEditPhaseList = lifecycleEditAccess || isOwner || isProjectResponsible || phaseListEditAccess
    const allowedToAddPopulation = lifecycleEditAccess || isOwner || isProjectResponsible || populateAddAccess
    const allowedToDeletePopulation = lifecycleEditAccess || isOwner || isProjectResponsible || populateDeleteAccess


    const accountUsers = (accountUserList && accountUserList[userData.accountId]) || []
    const projectUsers = (projectUserList && projectUserList[project.id]) || []

    const {
        taskModuleAccess,
        ccRequirementsModuleAccess,
    } = packageSettings

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>
                            {lang.inspector}
                            {
                                allowedToEditPhaseList
                                && <LabelButton onClick={onDelete}>{lang.delete}</LabelButton>
                            }
                        </Label>
                    </FormGroup>
                    <Delimiter size="sm" />

                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} readOnly={!allowedToEditPhaseList} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.objective}</Label>
                        <Textarea value={objective} onChange={setObjective} readOnly={!allowedToEditPhaseList} />
                        <Feedback>{validation && validation.objective}</Feedback>
                    </FormGroup>
                </Col>

                {
                    userData.accountId !== 1 && (
                        <Col xs={12}>
                            <Delimiter size="sm" />
                            <FormGroup>
                                <Label>{lang.responsible}</Label>
                                {
                                    allowedToEditPhaseList && (
                                        <UserDropdown
                                            selectedId={responsible || lifecycle.responsible}
                                            listIds={!project.id ? accountUsers : projectUsers}
                                            title={lang.change}
                                            onChange={(userId) => setResponsible(userId)}
                                            // lifecycle responsible is default value that can not be removed
                                            showDelete={responsible && responsible !== lifecycle.responsible}
                                        />
                                    )
                                }
                                {
                                    !allowedToEditPhaseList
                                    && <DivInput><UserIcon user={userList[responsible || lifecycle.responsible]} size="sm" /></DivInput>
                                }
                            </FormGroup>
                        </Col>
                    )
                }

                {
                    project.id && (
                        <Col xs={12}>
                            <Delimiter size="sm" />
                            <FormGroup>
                                <Label>{lang.inputDocuments}</Label>
                                <PopulateDocuments
                                    documents={inputDocuments}
                                    addAccess={allowedToAddPopulation}
                                    deleteAccess={allowedToDeletePopulation}
                                    onAdd={addInputDocument}
                                    onDelete={deleteInputDocument}
                                />
                            </FormGroup>

                            <Delimiter size="sm" />
                            <FormGroup>
                                <Label>{lang.outputDocuments}</Label>
                                <PopulateDocuments
                                    documents={outputDocuments}
                                    addAccess={allowedToAddPopulation}
                                    deleteAccess={allowedToDeletePopulation}
                                    onAdd={addOutputDocument}
                                    onDelete={deleteOutputDocument}
                                />
                            </FormGroup>

                            {
                                taskModuleAccess === YES && hasAccess(PRM_TASK, LIST) && ([
                                    <Delimiter key="delimiter" size="sm" />,
                                    <FormGroup key="formGroup">
                                        <Label>{lang.tasks}</Label>
                                        <LinkTasks
                                            linkedTasks={linkedTasks}
                                            editAccess={allowedToEditPhaseList}
                                            onAdd={addTasks}
                                            onDelete={deleteTask}
                                        />
                                    </FormGroup>,
                                ])
                            }

                            {
                                ccRequirementsModuleAccess === YES && hasProjectAccess(PRM_CC_REQUIREMENT, LIST) && ([
                                    <Delimiter key="delimiter" size="sm" />,
                                    <FormGroup key="formGroup">
                                        <Label>{lang.requirements}</Label>
                                        <LinkRequirements
                                            linkedRequirements={linkedRequirements}
                                            editAccess={allowedToEditPhaseList}
                                            onAdd={addRequirements}
                                            onDelete={deleteRequirement}
                                        />
                                    </FormGroup>,
                                ])
                            }
                        </Col>
                    )
                }
                {
                    (allowedToEditPhaseList || allowedToAddPopulation || allowedToDeletePopulation) && (
                        <Col xs={12}>
                            <Delimiter size="sm" />
                            <Button variant="success" row onClick={() => onSave()}>
                                {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                            </Button>
                        </Col>
                    )
                }
            </Row>
        </PageForm>
    )
}
export default Form
